import React, { FC, useContext, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { StatusOnboarding } from "../../common";
import { InvoiceDetailsDataModal } from "../../modals";
import { CURRENCY_MAP, DEFAULT_CRYPTO_CURRENCY } from "../../../constants";
import { UIActionContext } from "../../../contexts/UIActionContext";
import { selectInvoice } from "../../../store/reducers/invoice";
import {
  checkIssuerDetails,
  checkPayerDetails,
  checkJobAmounts,
  checkJobDescriptions,
  checkWallet,
} from "../../../utils/prepareData/invoiceFilledStatus";
import { useDisplayInvoiceWalletSection } from "../../../hooks/useDisplayInvoiceWalletSection";
import { useValidateInvoiceAmountThreshold } from "../../../hooks/useValidateInvoiceAmountThreshold";
import { CurrencyType } from "../../../types/InvoiceTypes";

export const InvoiceStatusCheck: FC = () => {
  const [showModal, setShowModal] = useState(false);
  const [side, setSide] = useState<"from" | "to">("from");
  const invoice = useSelector(selectInvoice);
  const showWalletForm = useDisplayInvoiceWalletSection(invoice);
  const uiActionContext = useContext(UIActionContext);

  const issuerDetailsStatus = useMemo(
    () => checkIssuerDetails(invoice),
    [invoice]
  );

  const payerDetailsStatus = useMemo(
    () => checkPayerDetails(invoice),
    [invoice]
  );

  const jobDescriptionsStatus = useMemo(
    () => checkJobDescriptions(invoice),
    [invoice]
  );

  const jobAmountsStatus = useMemo(() => checkJobAmounts(invoice), [invoice]);
  const walletStatus = useMemo(() => checkWallet(invoice), [invoice]);

  const { isValid: isValidAmountThreshold } =
    useValidateInvoiceAmountThreshold(invoice);

  if (!invoice) {
    return <></>;
  }

  const { user_is_issuer, outgoing_currency } = invoice;

  const userStatus = user_is_issuer ? issuerDetailsStatus : payerDetailsStatus;

  const counterPartyStatus = user_is_issuer
    ? payerDetailsStatus
    : issuerDetailsStatus;

  const counterPartyStatusLabel = user_is_issuer
    ? "Client details"
    : "Recipient details";

  const walletLabel =
    CURRENCY_MAP[outgoing_currency || DEFAULT_CRYPTO_CURRENCY].type ===
    CurrencyType.CRYPTO
      ? "Wallet"
      : "Bank account";

  const onOpenModal = (side: "from" | "to") => {
    setSide(side);
    setShowModal(true);
  };

  return (
    <>
      <ul>
        <li className="mb-4">
          <StatusOnboarding
            status={userStatus.valid ? "done" : "pending"}
            action={{
              label: "Add",
              onClick: () => onOpenModal(user_is_issuer ? "from" : "to"),
            }}
          >
            My details
          </StatusOnboarding>
        </li>

        <li className="mb-4">
          <StatusOnboarding
            status={counterPartyStatus.valid ? "done" : "pending"}
            action={{
              label: "Add",
              onClick: () => onOpenModal(user_is_issuer ? "to" : "from"),
            }}
          >
            {counterPartyStatusLabel}
          </StatusOnboarding>
        </li>

        <li className="mb-4">
          <StatusOnboarding
            status={jobDescriptionsStatus ? "done" : "pending"}
            action={{
              label: "Fill out",
              onClick: () =>
                uiActionContext?.focusOnFirstEmpty(/items\.(\d+)\.description/),
            }}
          >
            Job descriptions
          </StatusOnboarding>
        </li>

        <li className="mb-4">
          <StatusOnboarding
            status={
              isValidAmountThreshold
                ? jobAmountsStatus
                  ? "done"
                  : "pending"
                : "error"
            }
            action={{
              label: "Fill out",
              onClick: () =>
                uiActionContext?.focusOnFirstEmpty(/items\.(\d+)\.rate/),
            }}
          >
            Amount
          </StatusOnboarding>
        </li>

        {showWalletForm && (
          <li className="mb-4">
            <StatusOnboarding
              status={walletStatus ? "done" : "pending"}
              action={{
                label: "Choose",
                onClick: () => uiActionContext?.scrollTo("invoice-wallet-form"),
              }}
            >
              {walletLabel}
            </StatusOnboarding>
          </li>
        )}
      </ul>

      {showModal && (
        <InvoiceDetailsDataModal
          side={side}
          invoice={invoice}
          isOpen={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};
